import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../../store/authStore.js'
import { LogOut, User as UserIcon, ChevronDown, Layers, Users } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import opLogo from '../../assets/op.png'
import Footer from './Footer.jsx'

export default function Layout() {
  const user = useAuth(s => s.user)
  const logout = useAuth(s => s.logout)
  const nav = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false) }
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [menuOpen])

  const handleLogout = async () => {
    setMenuOpen(false)
    await logout()
    nav('/login')
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white sticky top-0 z-30 print:hidden">
        <div className="px-6 sm:px-10 h-[68px] grid grid-cols-[1fr_auto_1fr] items-center">
          <Link to="/" className="justify-self-start flex items-center shrink-0">
            <img src={opLogo} alt="O+P Consult" className="h-8 w-auto max-w-none object-contain shrink-0" />
          </Link>
          <Link
            to="/"
            className="justify-self-center font-serif text-[15px] sm:text-[22px] md:text-[24px] font-medium tracking-[-0.015em] text-ink hover:opacity-80 transition truncate min-w-0"
          >
            mindnet&thinsp;·&thinsp;academy
          </Link>
          {user && (
            <div className="relative justify-self-end" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="flex items-center gap-2.5 px-2 py-1 rounded-lg hover:bg-[#EAEBEF]/60 text-ink"
              >
                <span className="w-[34px] h-[34px] rounded-full bg-teal text-white grid place-items-center text-xs font-semibold">
                  {user.firstName?.[0]}{user.lastName?.[0]}
                </span>
                <span className="hidden sm:inline text-[13.5px]">Hallo {user.firstName}</span>
                <ChevronDown size={15} strokeWidth={1.6} className={`transition ${menuOpen ? 'rotate-180' : ''}`} />
              </button>
              {menuOpen && (
                <div role="menu" className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-card border border-[#EAEBEF] py-2 z-50">
                  <Link to="/profile" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-teal-50 text-ink">
                    <UserIcon size={15} strokeWidth={1.6} /> Profil
                  </Link>
                  <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-teal-50 text-ink">
                    <LogOut size={15} strokeWidth={1.6} /> Ausloggen
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {user && (
          <nav className="border-y border-[#EAEBEF] h-12 px-6 sm:px-10 flex items-center gap-7 text-[13.5px]">
            <NavItem to="/" icon={<Layers size={15} strokeWidth={1.6} />}>
              <span className="sm:hidden">Umfragen</span>
              <span className="hidden sm:inline">Meine Umfragen</span>
            </NavItem>
            {user.role === 'admin' && (
              <NavItem to="/admin/zugaenge" icon={<Users size={15} strokeWidth={1.6} />}>
                <span className="sm:hidden">Zugänge</span>
                <span className="hidden sm:inline">Zugänge verwalten</span>
              </NavItem>
            )}
          </nav>
        )}
      </header>

      <main className="flex-1 w-full px-6 sm:px-10 pt-16 pb-8 print:px-0 print:py-0">
        <Outlet />
      </main>

      <div className="mt-12 print:hidden">
        <Footer />
      </div>
    </div>
  )
}

function NavItem({ to, icon, children }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `flex items-center gap-2 h-full relative transition ${
          isActive
            ? 'text-ink font-medium after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-teal after:rounded-sm'
            : 'text-ink-soft hover:text-ink'
        }`
      }
    >
      {icon}
      {children}
    </NavLink>
  )
}
